import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api, { API_BASE, getImageUrl } from '../../utils/api';

interface UserSidebarProps {
    collapsed: boolean;
    mobileShow: boolean;
    setMobileShow: (show: boolean) => void;
}

const UserSidebar: React.FC<UserSidebarProps> = ({ collapsed, mobileShow, setMobileShow }) => {
    const location = useLocation();
    const { user, token } = useAuth();
    const currentPath = location.pathname;
    const [stats, setStats] = useState<{ [key: string]: number }>({});
    const [showBrokerLeads, setShowBrokerLeads] = useState(true);

    // Package info state
    const [packageInfo, setPackageInfo] = useState<{
        name: string;
        period: string;
        expiry: Date | null;
        startDate: Date | null;
        remainingProjects: number;
        remainingProperties: number;
        color: string;
        icon: string;
    } | null>(null);

    const role = user?.role || 'buyer';

    useEffect(() => {
        const fetchDynamicStats = async () => {
            if (!token || (role !== 'broker' && role !== 'agent')) return;
            try {
                const res = await fetch(`${API_BASE}/leads`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                const data = await res.json();
                if (Array.isArray(data)) {
                    const inquiryCount = data.filter((l: any) => l.status === 'New Inquiry' || l.status === 'Requirement').length;
                    const leadCount = data.filter((l: any) => l.status !== 'New Inquiry' && l.status !== 'Requirement').length;
                    setStats({
                        '/broker-inquiries': inquiryCount,
                        '/broker-leads': leadCount
                    });
                }
            } catch (error) {
                console.error('Error fetching sidebar stats:', error);
            }
        };

        const fetchSystemSettings = async () => {
            try {
                const data = await api.getSystemSettings();
                if (data) {
                    if (typeof data.showBrokerLeads !== 'undefined') setShowBrokerLeads(data.showBrokerLeads);
                }
            } catch (err) {
                console.error('Error fetching system settings for sidebar:', err);
            }
        };

        // Fetch package info for developer, broker, owner
        const fetchPackageInfo = async () => {
            if (!token || !['developer', 'broker', 'owner'].includes(role)) return;
            try {
                const profile = await api.getUserProfile(token);
                if (profile?.assignedPackage) {
                    setPackageInfo({
                        name: profile.assignedPackage.name,
                        period: profile.assignedPackage.period || '',
                        expiry: profile.packageExpiry ? new Date(profile.packageExpiry) : null,
                        startDate: profile.packageStartDate ? new Date(profile.packageStartDate) : null,
                        remainingProjects: profile.remainingProjects || 0,
                        remainingProperties: profile.remainingProperties || 0,
                        color: profile.assignedPackage.color || '#3b82f6',
                        icon: profile.assignedPackage.icon || 'fa-box',
                    });
                } else {
                    setPackageInfo(null);
                }
            } catch (err) {
                console.error('Error fetching package info:', err);
            }
        };

        fetchDynamicStats();
        fetchSystemSettings();
        fetchPackageInfo();
    }, [token, role]);

    // Calculate days remaining
    const getDaysRemaining = () => {
        if (!packageInfo?.expiry) return null; // null = unlimited
        const now = new Date();
        const diff = packageInfo.expiry.getTime() - now.getTime();
        return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
    };

    const daysRemaining = packageInfo ? getDaysRemaining() : undefined;

    const navItems = (() => {
        if (role === 'broker' || role === 'agent') {
            const items: any[] = [
                { label: 'Dashboard', path: '/broker-dashboard', icon: 'fa-chart-pie' },
                { label: 'Classified Leads', path: '/broker-classified-ads', icon: 'fa-bookmark' },
                { label: 'Saved Leads', path: '/saved-classified-leads', icon: 'fa-solid fa-bookmark' },
                { label: 'My Profile', path: '/broker-profile', icon: 'fa-user-gear' },
                { label: 'Add Project', path: '/broker-add-project', icon: 'fa-plus' },
                { label: 'My Projects', path: '/broker-my-projects', icon: 'fa-house-circle-check' },
                { label: 'Wishlist', path: '/broker-wishlist', icon: 'fa-heart' },
            ];
            if (showBrokerLeads) {
                items.push({ label: 'Leads', path: '/broker-leads', icon: 'fa-users-viewfinder', badge: stats['/broker-leads'] });
                items.push({ label: 'Inquiries', path: '/broker-inquiries', icon: 'fa-envelope-open-text', badge: stats['/broker-inquiries'] });
            }
            items.push({ label: 'Lead Plan', path: '/broker-buy-lead-plan', icon: 'fa-crown' });
            items.push({ label: 'Purchased Plans', path: '/broker-purchased-plans', icon: 'fa-receipt' });
            items.push({ label: 'My Package', path: '/my-package', icon: 'fa-crown' });
            items.push({ label: 'Security', path: '/broker-change-password', icon: 'fa-shield-halved' });
            return items;
        }

        if (role === 'owner') {
            return [
                { label: 'Dashboard', path: '/owner-dashboard', icon: 'fa-chart-pie' },
                { label: 'My Profile', path: '/my-profile', icon: 'fa-user-gear' },
                { label: 'Add Property', path: '/owner-add-property', icon: 'fa-plus' },
                { label: 'My Properties', path: '/owner-property', icon: 'fa-house' },
                { label: 'My Package', path: '/my-package', icon: 'fa-crown' },
                { label: 'Security', path: '/change-password', icon: 'fa-shield-halved' }
            ];
        }

        if (role === 'developer') {
            return [
                { label: 'Dashboard', path: '/developer-dashboard', icon: 'fa-chart-pie' },
                { label: 'My Profile', path: '/developer-profile', icon: 'fa-user-gear' },
                { label: 'Classified Leads', path: '/broker-classified-ads', icon: 'fa-bookmark' },
                { label: 'Saved Leads', path: '/saved-classified-leads', icon: 'fa-solid fa-bookmark' },
                { label: 'Add Project', path: '/developer-add-project', icon: 'fa-plus' },
                { label: 'Saved Drafts', path: '/developer-drafts', icon: 'fa-file-pen' },
                { label: 'My Projects', path: '/developer-my-property', icon: 'fa-house-circle-check' },
                { label: 'My Package', path: '/my-package', icon: 'fa-crown' },
                { label: 'Security', path: '/developer-change-password', icon: 'fa-shield-halved' }
            ];
        }

        const common = [
            { label: 'Dashboard', path: `/${role}/dashboard`, icon: 'fa-chart-pie' },
            { label: 'My Profile', path: '/my-profile', icon: 'fa-user-gear' },
        ];
        let roleSpecific: any[] = [];
        if (role === 'buyer' || role === 'tenant') {
            roleSpecific = [
                { label: 'Wishlist', path: '/bookmark-list', icon: 'fa-heart' },
                { label: 'Inquiries', path: '/buyer-inquiries', icon: 'fa-envelope-open-text' },
            ];
        }
        return [...common, ...roleSpecific, { label: 'Security', path: '/change-password', icon: 'fa-shield-halved' }];
    })();

    const showPackageCard = ['developer', 'broker', 'owner'].includes(role);

    return (
        <>
            <style dangerouslySetInnerHTML={{
                __html: `
                .admin-sidebar::-webkit-scrollbar {
                    width: 3px;
                }
                .admin-sidebar::-webkit-scrollbar-track {
                    background: transparent;
                }
                .admin-sidebar::-webkit-scrollbar-thumb {
                    background: rgba(255, 255, 255, 0.1);
                    border-radius: 10px;
                }
                .admin-sidebar:hover::-webkit-scrollbar-thumb {
                    background: rgba(255, 255, 255, 0.2);
                }
                @keyframes shimmer {
                    0% { background-position: -200% 0; }
                    100% { background-position: 200% 0; }
                }
                .pkg-shimmer {
                    background: linear-gradient(90deg, rgba(255,255,255,0.05) 25%, rgba(255,255,255,0.12) 50%, rgba(255,255,255,0.05) 75%);
                    background-size: 200% 100%;
                    animation: shimmer 2s infinite;
                }
            `}} />

            <aside className={`fixed md:relative inset-y-0 left-0 z-[1000] md:z-10 h-screen shrink-0 text-white transition-all duration-500 ease-[cubic-bezier(0.4,0,0.2,1)] flex flex-col shadow-2xl overflow-hidden ${collapsed ? 'w-24' : 'w-[280px]'} ${mobileShow ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}`} style={{ background: 'linear-gradient(to bottom, #05144b 0%, #4c1d95 100%)' }}>
                {/* Sidebar Header */}
                <div className={`flex items-center justify-center px-6 shrink-0 z-30 bg-transparent ${collapsed ? 'h-20 border-b border-white/[0.05]' : 'h-0'}`}>
                    <div className="flex items-center gap-3">
                        {collapsed && (
                            <div className="w-12 h-12 bg-blue-600 rounded-2xl flex items-center justify-center shadow-lg shadow-blue-600/30 text-white font-bold text-xl animate__animated animate__zoomIn shrink-0 mt-4">
                                <span>PG</span>
                            </div>
                        )}
                    </div>
                </div>

                {/* Profile Section */}
                {!collapsed && (
                    <div className="px-6 py-2 border-b border-white/[0.05] flex flex-col items-center">
                        <div className="w-20 h-20 rounded-full p-1 bg-white/10 shadow-md">
                            <div className="w-full h-full rounded-full overflow-hidden border-2 border-[#05144b] shadow-inner relative">
                                {user?.avatar ? (
                                    <img src={getImageUrl(user.avatar)} className="w-full h-full object-cover" alt="User" />
                                ) : (
                                    <div className="w-full h-full bg-gradient-to-br from-[#05144b] to-[#4c1d95] flex items-center justify-center text-white font-black text-xl uppercase">
                                        {user?.name?.charAt(0) || 'U'}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* Navigation */}
                <div className={`admin-sidebar flex-1 overflow-y-auto overflow-x-hidden pt-4 pb-4 transition-all duration-500 relative scroll-smooth`}>
                    <nav className="space-y-1">
                        {navItems && navItems.map((item) => {
                            const isActive = currentPath === item.path;
                            return (
                                <Link
                                    key={item.path}
                                    to={item.path}
                                    title={item.label}
                                    aria-label={item.label}
                                    onClick={() => setMobileShow(false)}
                                    className={`group flex items-center gap-3 py-3 px-4 transition-all duration-300 relative mx-3 rounded-xl ${isActive ? 'bg-white text-[#05144b] shadow-lg translate-x-1' : 'text-blue-100 hover:bg-white/10 hover:text-white'}`}
                                >
                                    <i className={`fa-solid ${item.icon} text-[20px] transition-colors duration-500 ${isActive ? 'text-[#05144b]' : 'text-blue-100/70 group-hover:text-white'}`}></i>
                                    {!collapsed && (
                                        <span className={`text-[14px] font-bold tracking-tight flex-1`}>
                                            {item.label}
                                        </span>
                                    )}
                                    {item.badge > 0 && (
                                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold shadow-lg ${isActive ? 'bg-[#05144b] text-white' : 'bg-rose-500 text-white'}`}>
                                            {item.badge}
                                        </span>
                                    )}
                                    {collapsed && item.badge > 0 && (
                                        <span className="absolute top-2 right-2 w-2 h-2 bg-rose-500 rounded-full border border-[#05144b]"></span>
                                    )}
                                </Link>
                            );
                        })}
                    </nav>

                    {/* ✅ MY PACKAGE CARD - Developer, Broker, Owner */}
                    {showPackageCard && !collapsed && (
                        <div className="mx-3 mt-5">
                            {/* Section Label */}
                            <div className="flex items-center gap-2 mb-2 px-1">
                                <div className="h-px flex-1 bg-white/10"></div>
                                <span className="text-[10px] font-bold text-white/40 uppercase tracking-widest">My Package</span>
                                <div className="h-px flex-1 bg-white/10"></div>
                            </div>

                            {packageInfo ? (
                                <div className="rounded-2xl overflow-hidden" style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)' }}>
                                    {/* Package Header */}
                                    <div className="px-4 pt-4 pb-3 flex items-center gap-3" style={{ background: 'rgba(255,255,255,0.05)' }}>
                                        <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0 shadow-lg"
                                            style={{ backgroundColor: packageInfo.color + '33', border: `1.5px solid ${packageInfo.color}55` }}>
                                            <i className={`fa-solid ${packageInfo.icon} text-sm`} style={{ color: packageInfo.color }}></i>
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-white font-black text-sm leading-tight truncate">{packageInfo.name}</p>
                                            <p className="text-white/50 text-[10px] font-semibold mt-0.5 truncate">{packageInfo.period || 'Standard Plan'}</p>
                                        </div>
                                        <span className="shrink-0 px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-wider bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
                                            Active
                                        </span>
                                    </div>

                                    {/* Days Remaining Bar */}
                                    <div className="px-4 py-3">
                                        {daysRemaining === null ? (
                                            // Unlimited / Lifetime
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-2">
                                                    <i className="fa-solid fa-infinity text-amber-400 text-sm"></i>
                                                    <span className="text-white font-black text-sm">Lifetime</span>
                                                </div>
                                                <span className="text-[10px] text-white/40 font-semibold">No Expiry</span>
                                            </div>
                                        ) : (
                                            <>
                                                <div className="flex items-center justify-between mb-1.5">
                                                    <span className="text-white/60 text-[10px] font-bold uppercase tracking-wider">Days Remaining</span>
                                                    <span className={`text-sm font-black ${(daysRemaining ?? 0) <= 7 ? 'text-rose-400' : (daysRemaining ?? 0) <= 30 ? 'text-amber-400' : 'text-emerald-400'}`}>
                                                        {daysRemaining} days
                                                    </span>
                                                </div>
                                                {/* Progress Bar */}
                                                {packageInfo.startDate && packageInfo.expiry && (
                                                    () => {
                                                        const total = packageInfo.expiry!.getTime() - packageInfo.startDate!.getTime();
                                                        const elapsed = new Date().getTime() - packageInfo.startDate!.getTime();
                                                        const pct = Math.max(0, Math.min(100, 100 - (elapsed / total) * 100));
                                                        return (
                                                            <div className="h-1.5 w-full rounded-full bg-white/10 overflow-hidden">
                                                                <div
                                                                    className={`h-full rounded-full transition-all duration-500 ${(daysRemaining ?? 0) <= 7 ? 'bg-rose-500' : (daysRemaining ?? 0) <= 30 ? 'bg-amber-400' : 'bg-emerald-400'}`}
                                                                    style={{ width: `${pct}%` }}
                                                                />
                                                            </div>
                                                        );
                                                    }
                                                )()}
                                                {packageInfo.expiry && (
                                                    <p className="text-white/30 text-[9px] font-medium mt-1.5">
                                                        Expires: {packageInfo.expiry.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                                                    </p>
                                                )}
                                            </>
                                        )}

                                        {/* Quota Info */}
                                        {(role === 'developer' || role === 'broker') && (
                                            <div className="mt-3 pt-3 border-t border-white/10 flex items-center justify-between">
                                                <div className="flex items-center gap-1.5">
                                                    <i className="fa-solid fa-building text-blue-300 text-[10px]"></i>
                                                    <span className="text-white/50 text-[10px] font-semibold">
                                                        {role === 'developer' ? 'Projects Left' : 'Properties Left'}
                                                    </span>
                                                </div>
                                                <span className="text-white font-black text-sm">
                                                    {role === 'developer'
                                                        ? (packageInfo.remainingProjects >= 999 ? '∞' : packageInfo.remainingProjects)
                                                        : (packageInfo.remainingProperties >= 999 ? '∞' : packageInfo.remainingProperties)
                                                    }
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ) : (
                                // No Package Assigned
                                <div className="rounded-2xl px-4 py-4 text-center pkg-shimmer" style={{ border: '1px dashed rgba(255,255,255,0.15)' }}>
                                    <div className="w-10 h-10 mx-auto rounded-full bg-white/10 flex items-center justify-center mb-2">
                                        <i className="fa-solid fa-crown text-white/30 text-sm"></i>
                                    </div>
                                    <p className="text-white/50 text-[11px] font-bold mb-0.5">No Package Active</p>
                                    <p className="text-white/30 text-[10px]">Contact admin to assign a package</p>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Collapsed mode - package icon only */}
                    {showPackageCard && collapsed && (
                        <div className="mx-2 mt-4">
                            <div title={packageInfo ? `${packageInfo.name} - ${daysRemaining === null ? 'Lifetime' : `${daysRemaining} days left`}` : 'No Package'}
                                className={`w-full aspect-square rounded-xl flex items-center justify-center cursor-default relative ${packageInfo ? 'bg-white/10' : 'bg-white/5'}`}>
                                <i className={`fa-solid fa-crown text-lg ${packageInfo ? 'text-amber-400' : 'text-white/20'}`}></i>
                                {packageInfo && daysRemaining != null && daysRemaining <= 7 && (
                                    <span className="absolute top-1 right-1 w-2 h-2 bg-rose-500 rounded-full border border-[#05144b]"></span>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </aside>
        </>
    );
};

export default UserSidebar;
